import React, { useState, useEffect, useCallback } from 'react';
import { Spinner, Button, Form, Alert } from 'react-bootstrap';
import { FaCogs, FaSync, FaRedo, FaEdit, FaSave, FaTimes, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { fetchKnowledgeParameters } from '../../services/knowledgeBaseApi';
import { makeAuthenticatedRequest } from '../../services/api';

// ---------------------------------------------------------------------------
// Helper: format a parameter_name for display
// ---------------------------------------------------------------------------
const formatParamName = (name) => {
  if (!name) return '';
  return name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// ---------------------------------------------------------------------------
// Helper: group parameters by category then subcategory
// Returns Map<category, Map<subcategory, parameter[]>>  – both sorted alpha
// ---------------------------------------------------------------------------
const groupParameters = (parameters) => {
  const grouped = new Map();

  parameters.forEach((param) => {
    const cat = param.category || 'other';
    const sub = param.subcategory || '(no subcategory)';

    if (!grouped.has(cat)) grouped.set(cat, new Map());
    const catMap = grouped.get(cat);

    if (!catMap.has(sub)) catMap.set(sub, []);
    catMap.get(sub).push(param);
  });

  // Sort categories alphabetically
  const sortedCats = new Map(
    [...grouped.entries()].sort(([a], [b]) => a.localeCompare(b))
  );

  // Sort subcategories within each category, putting '(no subcategory)' first
  sortedCats.forEach((subMap, cat) => {
    const sortedSubs = new Map(
      [...subMap.entries()].sort(([a], [b]) => {
        if (a === '(no subcategory)') return -1;
        if (b === '(no subcategory)') return 1;
        return a.localeCompare(b);
      })
    );
    sortedCats.set(cat, sortedSubs);
  });

  return sortedCats;
};

// ---------------------------------------------------------------------------
// Helper: validate parameter value based on data type
// ---------------------------------------------------------------------------
const validateParameterValue = (value, dataType, minValue, maxValue) => {
  const trimmedValue = String(value).trim();
  
  switch (dataType?.toLowerCase()) {
    case 'float': {
      const num = parseFloat(trimmedValue);
      if (isNaN(num)) return { valid: false, error: 'Must be a valid decimal number' };
      if (minValue != null && num < parseFloat(minValue)) {
        return { valid: false, error: `Must be at least ${minValue}` };
      }
      if (maxValue != null && num > parseFloat(maxValue)) {
        return { valid: false, error: `Must be at most ${maxValue}` };
      }
      return { valid: true, value: trimmedValue };
    }
    
    case 'int': {
      const num = parseInt(trimmedValue, 10);
      if (isNaN(num) || !Number.isInteger(parseFloat(trimmedValue))) {
        return { valid: false, error: 'Must be a valid integer' };
      }
      if (minValue != null && num < parseInt(minValue, 10)) {
        return { valid: false, error: `Must be at least ${minValue}` };
      }
      if (maxValue != null && num > parseInt(maxValue, 10)) {
        return { valid: false, error: `Must be at most ${maxValue}` };
      }
      return { valid: true, value: String(num) };
    }
    
    case 'bool': {
      const lower = trimmedValue.toLowerCase();
      if (['true', '1', 'yes', 'on'].includes(lower)) {
        return { valid: true, value: 'true' };
      } else if (['false', '0', 'no', 'off'].includes(lower)) {
        return { valid: true, value: 'false' };
      }
      return { valid: false, error: 'Must be true/false, 1/0, yes/no, or on/off' };
    }
    
    case 'json': {
      if (!trimmedValue) return { valid: true, value: trimmedValue };
      try {
        JSON.parse(trimmedValue);
        return { valid: true, value: trimmedValue };
      } catch (e) {
        return { valid: false, error: 'Must be valid JSON' };
      }
    }
    
    case 'string':
    default:
      return { valid: true, value: trimmedValue };
  }
};

// ---------------------------------------------------------------------------
// DataTypeBadge
// ---------------------------------------------------------------------------
const DataTypeBadge = ({ type }) => {
  const cls = `config-data-type-badge config-data-type-${type || 'string'}`;
  return <span className={cls}>{type || 'string'}</span>;
};

// ---------------------------------------------------------------------------
// BoolValue – coloured display for boolean parameters
// ---------------------------------------------------------------------------
const BoolValue = ({ value }) => {
  const isTrue = String(value).toLowerCase() === 'true';
  return (
    <span style={{ color: isTrue ? '#4ade80' : '#94a3b8', fontWeight: 600 }}>
      {isTrue ? 'true' : 'false'}
    </span>
  );
};

// ---------------------------------------------------------------------------
// ParameterRow - Now with edit/save functionality
// ---------------------------------------------------------------------------
const ParameterRow = ({ param, onSave, onCancel }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(param.parameter_value || '');
  const [validationError, setValidationError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const range =
    param.min_value != null || param.max_value != null
      ? `${param.min_value ?? '—'} – ${param.max_value ?? '—'}`
      : '—';

  const handleEdit = () => {
    setIsEditing(true);
    setEditValue(param.parameter_value || '');
    setValidationError(null);
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditValue(param.parameter_value || '');
    setValidationError(null);
    setSaveError(null);
    if (onCancel) onCancel();
  };

  const handleSave = async () => {
    // Client-side validation
    const validation = validateParameterValue(
      editValue,
      param.data_type,
      param.min_value,
      param.max_value
    );

    if (!validation.valid) {
      setValidationError(validation.error);
      return;
    }

    setValidationError(null);
    setIsSaving(true);
    setSaveError(null);

    try {
      const response = await makeAuthenticatedRequest('POST', '/knowledge/configure-knowledge/', {
        action: 'update',
        parameters: {
          parameter_id: param.parameter_id,
          parameter_value: validation.value,
          modified_by: 'admin',
          reason: 'Updated via Config UI'
        }
      });

      // Parse response if needed
      let data = response;
      if (response && typeof response.body === 'string') {
        try {
          data = JSON.parse(response.body);
        } catch (e) {
          console.error('Failed to parse response:', e);
        }
      } else if (response && typeof response.body === 'object' && response.body !== null) {
        data = response.body;
      }

      if (data.success) {
        // Success - show green flash and exit edit mode
        setSaveSuccess(true);
        setTimeout(() => {
          setIsEditing(false);
          setSaveSuccess(false);
          if (onSave) onSave(data.parameter || param, data.previous_value);
        }, 1000);
      } else {
        // API returned error
        setSaveError(data.validation_error || data.message || 'Failed to update parameter');
      }
    } catch (error) {
      console.error('Error saving parameter:', error);
      setSaveError(error.message || 'Failed to update parameter');
    } finally {
      setIsSaving(false);
    }
  };

  const renderValue = () => {
    if (isEditing) {
      // Render appropriate input based on data type
      const inputType = param.data_type === 'int' || param.data_type === 'float' 
        ? 'number' 
        : 'text';
      
      const step = param.data_type === 'float' ? '0.01' : param.data_type === 'int' ? '1' : undefined;

      if (param.data_type === 'bool') {
        return (
          <Form.Select
            size="sm"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="bg-dark text-white border-info"
            style={{ 
              width: '100%',
              borderWidth: '2px',
              borderColor: validationError ? '#ef4444' : '#0ea5e9'
            }}
          >
            <option value="true">true</option>
            <option value="false">false</option>
          </Form.Select>
        );
      }

      return (
        <Form.Control
          type={inputType}
          size="sm"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          step={step}
          min={param.min_value}
          max={param.max_value}
          className="bg-dark text-white"
          style={{ 
            width: '100%',
            borderWidth: '2px',
            borderColor: validationError ? '#ef4444' : '#0ea5e9'
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSave();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              handleCancelEdit();
            }
          }}
        />
      );
    }

    // Read-only view
    if (param.data_type === 'bool') {
      return <BoolValue value={param.parameter_value} />;
    }
    return <span className="config-value-text">{param.parameter_value}</span>;
  };

  const rowClassName = `config-row${saveSuccess ? ' config-row-success' : ''}${isEditing ? ' config-row-editing' : ''}`;

  return (
    <>
      <div className={rowClassName}>
        {/* Parameter Name */}
        <div className="config-cell config-cell-name">
          {formatParamName(param.parameter_name)}
        </div>

        {/* Value (with edit input if editing) */}
        <div className="config-cell config-cell-value" style={{ position: 'relative' }}>
          {renderValue()}
        </div>

        {/* Type */}
        <div className="config-cell config-cell-type">
          <DataTypeBadge type={param.data_type} />
        </div>

        {/* Default */}
        <div className="config-cell config-cell-default">
          <span className="config-default-text">{param.default_value}</span>
        </div>

        {/* Range */}
        <div className="config-cell config-cell-range">
          <span className="config-range-text">{range}</span>
        </div>

        {/* Description */}
        <div className="config-cell config-cell-desc" title={param.description}>
          <span className="config-desc-text">{param.description}</span>
        </div>

        {/* Actions */}
        <div className="config-cell config-cell-actions" style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          {!isEditing ? (
            <Button
              variant="outline-info"
              size="sm"
              onClick={handleEdit}
              style={{ 
                padding: '4px 10px', 
                fontSize: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <FaEdit size={12} /> Edit
            </Button>
          ) : (
            <>
              <Button
                variant="success"
                size="sm"
                onClick={handleSave}
                disabled={isSaving}
                style={{ 
                  padding: '4px 10px', 
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                {isSaving ? (
                  <Spinner animation="border" size="sm" style={{ width: '12px', height: '12px' }} />
                ) : (
                  <>
                    {saveSuccess ? <FaCheckCircle size={12} /> : <FaSave size={12} />}
                    {saveSuccess ? 'Saved' : 'Save'}
                  </>
                )}
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleCancelEdit}
                disabled={isSaving}
                style={{ 
                  padding: '4px 10px', 
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <FaTimes size={12} /> Cancel
              </Button>
            </>
          )}
        </div>
      </div>
      
      {/* Validation/Save Error Row */}
      {(validationError || saveError) && isEditing && (
        <div className="config-row-error">
          <div className="config-cell" style={{ gridColumn: '1 / -1' }}>
            <Alert variant="danger" className="mb-0 py-1 px-2" style={{ fontSize: '0.75rem' }}>
              <FaExclamationTriangle size={12} className="me-2" />
              {validationError || saveError}
            </Alert>
          </div>
        </div>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Table header
// ---------------------------------------------------------------------------
const TableHeader = () => (
  <div className="config-row config-row-header">
    <div className="config-cell config-cell-name config-th">Parameter</div>
    <div className="config-cell config-cell-value config-th">Value</div>
    <div className="config-cell config-cell-type config-th">Type</div>
    <div className="config-cell config-cell-default config-th">Default</div>
    <div className="config-cell config-cell-range config-th">Range</div>
    <div className="config-cell config-cell-desc config-th">Description</div>
    <div className="config-cell config-cell-actions config-th">Actions</div>
  </div>
);

// ---------------------------------------------------------------------------
// Main ScoringConfiguration component
// ---------------------------------------------------------------------------
const ScoringConfiguration = () => {
  // ---------- state ----------
  const [configParameters, setConfigParameters] = useState([]);
  const [configCategories, setConfigCategories] = useState([]);
  const [configPagination, setConfigPagination] = useState({});
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState(null);
  const [selectedConfigCategory, setSelectedConfigCategory] = useState(null);
  const [configSearchTerm, setConfigSearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState(null);

  // ---------- data loading ----------
  const loadConfigParameters = useCallback(async () => {
    if (configLoading) return; // prevent duplicate in-flight calls
    setConfigLoading(true);
    setConfigError(null);
    setSuccessMessage(null);
    try {
      const { parameters, categories, pagination } = await fetchKnowledgeParameters();
      setConfigParameters(parameters);
      setConfigCategories(categories);
      setConfigPagination(pagination);
    } catch (err) {
      console.error('loadConfigParameters error:', err);
      setConfigError(err?.message || 'Failed to load system parameters. Please try again.');
    } finally {
      setConfigLoading(false);
    }
  }, [configLoading]);

  // Load once on mount
  useEffect(() => {
    if (configParameters.length === 0 && !configLoading) {
      loadConfigParameters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-hide success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // ---------- event handlers ----------
  const handleParameterSaved = (updatedParam, previousValue) => {
    // Update the parameter in the list
    setConfigParameters(prev => 
      prev.map(p => p.parameter_id === updatedParam.parameter_id ? updatedParam : p)
    );
    
    // Show success message
    setSuccessMessage(
      `Parameter "${formatParamName(updatedParam.parameter_name)}" updated successfully. ` +
      `Previous value: ${previousValue}`
    );
  };

  // ---------- derived display data ----------
  const totalCount =
    configPagination?.total_count != null
      ? configPagination.total_count
      : configParameters.length;

  // Build sorted category list for filter pills
  const sortedCategories = [...configCategories].sort((a, b) =>
    a.category.localeCompare(b.category)
  );

  // Apply category + search filters
  const filteredParameters = configParameters.filter((param) => {
    const matchesCategory =
      selectedConfigCategory === null || param.category === selectedConfigCategory;

    if (!matchesCategory) return false;

    if (configSearchTerm.trim() === '') return true;

    const term = configSearchTerm.toLowerCase();
    return (
      (param.parameter_name || '').toLowerCase().includes(term) ||
      (param.description || '').toLowerCase().includes(term) ||
      (param.parameter_value || '').toLowerCase().includes(term)
    );
  });

  const grouped = groupParameters(filteredParameters);

  // ---------- render helpers ----------
  const renderCategoryPills = () => (
    <div className="config-category-filter">
      {/* All pill */}
      <button
        className={`config-category-pill${selectedConfigCategory === null ? ' active' : ''}`}
        onClick={() => setSelectedConfigCategory(null)}
      >
        All
        <span className="config-pill-count">{configParameters.length}</span>
      </button>

      {sortedCategories.map((cat) => (
        <button
          key={cat.category}
          className={`config-category-pill${
            selectedConfigCategory === cat.category ? ' active' : ''
          }`}
          onClick={() => setSelectedConfigCategory(cat.category)}
        >
          {cat.category}
          <span className="config-pill-count">{cat.param_count}</span>
        </button>
      ))}
    </div>
  );

  const renderGroupedTable = () => {
    if (grouped.size === 0) {
      return (
        <div className="config-empty">
          <FaCogs size={32} className="mb-2" style={{ opacity: 0.4 }} />
          <p>No parameters found</p>
          {configSearchTerm || selectedConfigCategory ? (
            <p className="config-empty-hint">Try clearing filters or search term.</p>
          ) : null}
        </div>
      );
    }

    const sections = [];

    grouped.forEach((subMap, category) => {
      sections.push(
        <div key={category} className="config-category-block">
          <div className="config-category-header">
            <span className="config-category-label">{category}</span>
            <span className="config-category-count">
              {[...subMap.values()].reduce((sum, arr) => sum + arr.length, 0)} params
            </span>
          </div>

          {(() => {
            const subSections = [];
            subMap.forEach((params, subcategory) => {
              subSections.push(
                <div key={subcategory} className="config-subcategory-block">
                  {subcategory !== '(no subcategory)' && (
                    <div className="config-subcategory-header">{subcategory}</div>
                  )}
                  <div className="config-params-table">
                    <TableHeader />
                    {params.map((param) => (
                      <ParameterRow 
                        key={param.parameter_id} 
                        param={param}
                        onSave={handleParameterSaved}
                      />
                    ))}
                  </div>
                </div>
              );
            });
            return subSections;
          })()}
        </div>
      );
    });

    return sections;
  };

  // ---------- render ----------
  return (
    <div className="config-parameters-container">
      {/* ---- Header ---- */}
      <div className="config-header">
        <div>
          <h2 className="config-header-title">
            <FaCogs className="me-2" style={{ color: 'var(--electric-cyan)' }} />
            System Parameters
          </h2>
          {configParameters.length > 0 && (
            <p className="config-header-subtitle">
              {totalCount} parameter{totalCount !== 1 ? 's' : ''} across{' '}
              {configCategories.length} categor{configCategories.length !== 1 ? 'ies' : 'y'}
            </p>
          )}
        </div>
        <Button
          variant="outline-info"
          size="sm"
          className="config-refresh-btn"
          disabled={configLoading}
          onClick={loadConfigParameters}
        >
          {configLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              Loading…
            </>
          ) : (
            <>
              <FaSync className="me-1" />
              Refresh
            </>
          )}
        </Button>
      </div>

      {/* ---- Success message ---- */}
      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)} className="mb-3">
          <FaCheckCircle className="me-2" />
          {successMessage}
        </Alert>
      )}

      {/* ---- Error banner ---- */}
      {configError && (
        <div className="config-error">
          <span className="config-error-msg">⚠ {configError}</span>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={loadConfigParameters}
            disabled={configLoading}
            className="ms-3"
          >
            <FaRedo className="me-1" />
            Retry
          </Button>
        </div>
      )}

      {/* ---- Loading state (initial / no data yet) ---- */}
      {configLoading && configParameters.length === 0 && (
        <div className="config-loading">
          <Spinner animation="border" variant="info" />
          <p className="mt-3">Loading system parameters…</p>
        </div>
      )}

      {/* ---- Filters + table (shown once we have data, even while refreshing) ---- */}
      {configParameters.length > 0 && (
        <>
          {/* Category filter pills */}
          {renderCategoryPills()}

          {/* Search bar */}
          <div className="config-search-bar">
            <input
              type="text"
              className="config-search-input"
              placeholder="Search parameters…"
              value={configSearchTerm}
              onChange={(e) => setConfigSearchTerm(e.target.value)}
            />
          </div>

          {/* Refresh overlay spinner */}
          {configLoading && (
            <div className="config-loading config-loading-overlay">
              <Spinner animation="border" variant="info" size="sm" />
              <span className="ms-2">Refreshing…</span>
            </div>
          )}

          {/* Grouped parameter table */}
          <div className="config-table-scroll">
            {renderGroupedTable()}
          </div>
        </>
      )}

      {/* ---- Empty state after successful fetch with no filters ---- */}
      {!configLoading && !configError && configParameters.length === 0 && (
        <div className="config-empty">
          <FaCogs size={40} className="mb-3" style={{ opacity: 0.3 }} />
          <p>No parameters available.</p>
          <Button
            variant="outline-info"
            size="sm"
            onClick={loadConfigParameters}
          >
            <FaSync className="me-1" />
            Load Parameters
          </Button>
        </div>
      )}
    </div>
  );
};

export default ScoringConfiguration;